import React, {FC, useCallback, useContext} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Form, Formik, Field, FormikProps} from 'formik';
import {useTranslation} from 'react-i18next';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MenuItem from '@mui/material/MenuItem';

import RedirectSvgIcon from 'assets/icons/buttons/RedirectSvgIcon';

import {FormSectionMoveRequestFormType, FormPageTypes} from 'appRedux/actions/forms/types';
import {FORM_SECTION_MOVE} from 'appRedux/actions/forms';
import {RootReducer} from 'appRedux/reducers';

import {AlertContext} from 'contexts/alert/context';

import AgentSaveButton from 'components/AgentScreenComponents/_buttons/AgentSaveButton';
import {getCurrentPageBySection, checkIsTemplateFieldsPresented} from 'components/Forms/FormBuilder/SectionForm/helper';
import FormikSelectInput from 'components/AgentScreenComponents/_form/FormBuilderSelectInput';

import {preventSendForm} from 'helpers/index';

interface MoveFormSectionType {
    pageId: number;
    currentSectionId: number;
    closeModal: () => void;
}

const MoveFormSection: FC<MoveFormSectionType> = ({pageId, currentSectionId, closeModal}) => {
    const [t] = useTranslation();
    const dispatch = useDispatch();

    const {showAlert} = useContext(AlertContext);

    const formSectionMove = useCallback(data => dispatch({type: FORM_SECTION_MOVE.REQUEST, payload: data}), [dispatch]);

    const {
        admin: {
            formInfo: {pages},
        },
    } = useSelector<RootReducer>((state: RootReducer) => state) as RootReducer;

    const currentPage = getCurrentPageBySection(pages, currentSectionId);
    const isTemplateFieldsPresented = checkIsTemplateFieldsPresented(pages, currentSectionId);

    return (
        <Formik
            initialValues={{
                pageId,
            }}
            onSubmit={(values: FormSectionMoveRequestFormType) => {
                formSectionMove({
                    id: currentSectionId,
                    ...values,
                    showAlert,
                    callback: closeModal,
                });
            }}
        >
            {(formik: FormikProps<FormSectionMoveRequestFormType>) => {
                const filteredPages =
                    isTemplateFieldsPresented && currentPage
                        ? pages.filter(page => page.priority > currentPage.priority)
                        : pages;
                return (
                    <Form onKeyDown={preventSendForm}>
                        {isTemplateFieldsPresented && (
                            <Alert sx={{mb: 2}} severity={'info'}>
                                <Typography>{t('orguser.forms.moveSectionWithFieldTemplateTypeWarning')}</Typography>
                            </Alert>
                        )}
                        <Field
                            as="select"
                            required
                            name={'pageId'}
                            label={t('orguser.forms.formField.selectPage')}
                            options
                            component={FormikSelectInput}
                            fullwidth
                        >
                            {filteredPages.map((item: FormPageTypes, index: number) => {
                                const {title, id} = item;
                                if (id !== pageId) {
                                    return (
                                        <MenuItem key={`page-${index}`} value={id}>
                                            <Typography>{title}</Typography>
                                        </MenuItem>
                                    );
                                }
                                return null;
                            })}
                        </Field>
                        <Box sx={{display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', mt: 2}}>
                            <AgentSaveButton
                                isSubmit
                                isDisabled={!formik.values.pageId}
                                icon={<RedirectSvgIcon isActive />}
                                title={t('common.buttons.move')}
                            />
                        </Box>
                    </Form>
                );
            }}
        </Formik>
    );
};

export default MoveFormSection;
